// pages/WhatsappTemplates/TemplateForm.jsx
import { useState, useEffect, useRef } from "react";

// Variables available per target — shown as clickable chips
const TARGET_VARS = {
    CUSTOMER: ["customer_name","unique_order_id","total","payment_mode","restaurant_name","items","actual_delivery_charge","payable","order_comment", "reason"],
    OWNER:    ["customer_name","unique_order_id","total","payment_mode","items","actual_delivery_charge","order_comment", "reason"],
    STORE:    ["customer_name","unique_order_id","total","payment_mode","items","actual_delivery_charge","restaurant_name","order_comment", "reason"],
    DRIVER:   ["customer_name","unique_order_id","restaurant_name","restaurant_phone","actual_delivery_charge", "reason"],
    ADMIN:    ["customer_name","unique_order_id","total","payment_mode","restaurant_name","customer_phone","items", "reason"],
    OTP:      ["otp", "reason"],
    CANCEL:   ["customer_name","unique_order_id","restaurant_name","total","order_comment", "reason"],
};

// STATUS2–STATUS11 all share the same vars
const STATUS_VARS = ["customer_name","unique_order_id","restaurant_name","total","target"];

// Preview sample values
const SAMPLES = {
    unique_order_id: "TF-2847",
    customer_name:   "Rahul Sharma",
    restaurant_name: "Spice Garden",
    restaurant_phone:"+91 98765 43210",
    total:           "485",
    payment_mode:    "Online",
    actual_delivery_charge: "30",
    payable:         "455",
    sub_total:       "455",
    wallet_amount:   "30",
    otp:             "473821",
    order_comment:   "Extra spicy please",
    items:           "(2) Butter Chicken @180 = ₹360\n(1) Naan @25 = ₹25",
    target:          "CUSTOMER",
    customer_phone:  "+91 98765 12345",
    reason: "Some reason"
};

function renderPreview(body) {
    let out = body;
    Object.entries(SAMPLES).forEach(([k, v]) => {
        out = out.replaceAll(`{${k}}`, v);
    });
    return out;
}

export default function TemplateForm({ initial, targets, onSave, onCancel }) {
    const isEdit = !!initial;

    const [name,     setName]     = useState(initial?.name      ?? "");
    const [target,   setTarget]   = useState(initial?.target    ?? "CUSTOMER");
    const [body,     setBody]     = useState(initial?.body      ?? "");
    const [isActive, setIsActive] = useState(initial?.is_active ?? true);
    const [saving,   setSaving]   = useState(false);
    const [errors,   setErrors]   = useState({});

    const bodyRef = useRef(null);

    // Reset form when switching between templates
    useEffect(() => {
        setName(initial?.name      ?? "");
        setTarget(initial?.target  ?? "CUSTOMER");
        setBody(initial?.body      ?? "");
        setIsActive(initial?.is_active ?? true);
        setErrors({});
    }, [initial]);

    const vars = target.startsWith("STATUS")
        ? STATUS_VARS
        : (TARGET_VARS[target] ?? []);

    const insertVar = (v) => {
        const ta = bodyRef.current;
        if (!ta) return;
        const s = ta.selectionStart;
        const e = ta.selectionEnd;
        const next = body.substring(0, s) + `{${v}}` + body.substring(e);
        setBody(next);
        // restore cursor after react re-render
        requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(s + v.length + 2, s + v.length + 2);
        });
    };

    const validate = () => {
        const errs = {};
        if (!name.trim())   errs.name   = "Required";
        if (!/^[a-z0-9_]+$/.test(name)) errs.name = "Lowercase letters, numbers and _ only";
        if (!target)        errs.target = "Required";
        if (!body.trim())   errs.body   = "Required";
        return errs;
    };

    const handleSubmit = async () => {
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }
        setSaving(true);
        await onSave({ name, target, body, is_active: isActive });
        setSaving(false);
    };

    return (
        <div className="wt-modal-overlay">
            <div className="wt-form-card">
                <div className="wt-form-card">
                    <div className="wt-form-head">
                        <span className="wt-section-title">{isEdit ? "Edit Template" : "New Template"}</span>
                        <button className="wt-btn-ghost" onClick={onCancel}>Cancel</button>
                    </div>

                    <div className="wt-form-body">
                        {/* Row 1: name + status */}
                        <div className="wt-form-row">
                            <div className="wt-form-group">
                                <label className="wt-label">
                                    Template name <span className="wt-badge-req">required</span>
                                </label>
                                <input
                                    type="text"
                                    className={`wt-input${errors.name ? " wt-input--err" : ""}`}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. new_order_customer"
                                />
                                {errors.name && <span className="wt-err">{errors.name}</span>}
                                <span className="wt-hint">Lowercase, underscores only. Used in logs.</span>
                            </div>

                            <div className="wt-form-group">
                                <label className="wt-label">Status</label>
                                <div className="wt-toggle-row">
                                    <label className="wt-toggle">
                                        <input
                                            type="checkbox"
                                            checked={isActive}
                                            onChange={(e) => setIsActive(e.target.checked)}
                                        />
                                        <span className="wt-toggle-track"/>
                                        <span className="wt-toggle-thumb"/>
                                    </label>
                                    <span className={`wt-status-text${isActive ? " wt-status-text--on" : ""}`}>
                                {isActive ? "Active" : "Inactive"}
                            </span>
                                </div>
                            </div>
                        </div>

                        {/* Target select */}
                        <div className="wt-form-group">
                            <label className="wt-label">
                                Target <span className="wt-badge-req">required</span>
                            </label>
                            <select
                                className={`wt-input${errors.target ? " wt-input--err" : ""}`}
                                value={target}
                                onChange={(e) => setTarget(e.target.value)}
                            >
                                {targets.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <span className="wt-hint">Determines which recipient gets this message.</span>
                        </div>

                        <div className="wt-divider"/>

                        {/* Var chips */}
                        <div className="wt-form-group">
                            <label className="wt-label">Available variables — click to insert</label>
                            <div className="wt-chips">
                                {vars.map((v) => (
                                    <button
                                        key={v}
                                        type="button"
                                        className="wt-chip"
                                        onClick={() => insertVar(v)}
                                    >
                                        {`{${v}}`}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Body */}
                        <div className="wt-form-group">
                            <label className="wt-label">
                                Message body <span className="wt-badge-req">required</span>
                            </label>
                            <textarea
                                ref={bodyRef}
                                className={`wt-textarea${errors.body ? " wt-input--err" : ""}`}
                                rows={6}
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                placeholder={`Hi {customer_name}, your order #{unique_order_id} has been placed at {restaurant_name}.`}
                            />
                            {errors.body && <span className="wt-err">{errors.body}</span>}
                        </div>

                        <div className="wt-divider"/>

                        {/* Preview */}
                        {body.trim() && (
                            <div className="wt-form-group">
                                <label className="wt-label">Preview (with sample values)</label>
                                <div className="wt-preview">
                                    <div className="wt-preview-label">WhatsApp</div>
                                    <div className="wt-preview-bubble">
                                        {renderPreview(body)}
                                    </div>
                                    <div className="wt-preview-time">10:24 ✓✓</div>
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="wt-form-actions">
                            <button
                                className="wt-btn-primary"
                                onClick={handleSubmit}
                                disabled={saving}
                            >
                                {saving ? "Saving…" : isEdit ? "Update Template" : "Create Template"}
                            </button>
                            <button className="wt-btn-ghost" onClick={onCancel}>Discard</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}